import React, { useEffect, useState } from 'react';
import { Search, MessageSquare, TrendingUp, Award, User } from 'lucide-react';
import { fetchUsers } from '../api/usersApi';

const AdminInteractions = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const data = await fetchUsers();
            // Sort by interactions descending
            const sortedData = [...data].sort((a, b) => (b.interactions || 0) - (a.interactions || 0));
            setUsers(sortedData);
        } catch (error) {
            console.error('Error loading users for interactions:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = users.filter(user =>
        user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.essenNumber?.toString().includes(searchTerm)
    );

    const totalInteractions = users.reduce((acc, user) => acc + (user.interactions || 0), 0);

    const activeUsers = users.filter(user => {
        if (!user.subscriptionExpirationDate) return false;
        const expDate = new Date(user.subscriptionExpirationDate + 'T00:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return expDate >= today;
    });

    const activeInteractions = activeUsers.reduce((acc, user) => acc + (user.interactions || 0), 0);
    const avgInteractions = activeUsers.length > 0 ? (activeInteractions / activeUsers.length).toFixed(1) : 0;

    const topPerformer = users.length > 0 ? users[0] : null;

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Interacciones de WhatsApp</h1>
                    <p className="text-gray-500 mt-1">Ranking de usuarios por actividad en el catálogo</p>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="p-3 bg-purple-100 rounded-lg text-purple-600">
                        <MessageSquare className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Total Consultas</p>
                        <p className="text-2xl font-bold text-gray-900">{totalInteractions}</p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="p-3 bg-green-100 rounded-lg text-green-600">
                        <TrendingUp className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Promedio x Usuario (Activos)</p>
                        <p className="text-2xl font-bold text-gray-900">
                            {avgInteractions}
                        </p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="p-3 bg-yellow-100 rounded-lg text-yellow-600">
                        <Award className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Líder Actividad</p>
                        <p className="text-lg font-bold text-gray-900 truncate max-w-[150px]">
                            {topPerformer ? `${topPerformer.firstName}` : '-'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Podium Section */}
            {users.length >= 3 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col items-center mt-8 overflow-hidden">
                    <div className="flex justify-center items-end gap-3 md:gap-8 pt-6">
                        {/* 2nd Place */}
                        <div className="flex flex-col items-center w-24 md:w-28">
                            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-gray-100 to-gray-300 border-4 border-white flex justify-center items-center mb-2 shadow-lg z-10 relative">
                                <span className="absolute -top-2 -right-2 bg-gray-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-white">2</span>
                                <User className="w-6 h-6 md:w-8 md:h-8 text-gray-600" />
                            </div>
                            <p className="font-bold text-gray-800 text-center truncate w-full text-xs md:text-sm">{users[1].firstName}</p>
                            <p className="text-xs font-semibold text-purple-600 mb-2">{users[1].interactions || 0} <span className="font-normal text-[10px] text-gray-400">int.</span></p>
                            <div className="w-full h-16 md:h-20 bg-gradient-to-t from-gray-300 to-gray-100 rounded-t-lg shadow-inner border-t border-gray-400"></div>
                        </div>

                        {/* 1st Place */}
                        <div className="flex flex-col items-center w-28 md:w-36 relative">
                            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-yellow-200 to-yellow-400 border-4 border-white flex justify-center items-center mb-2 shadow-xl z-10 relative">
                                <span className="absolute -top-2 -right-2 bg-yellow-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">1</span>
                                <User className="w-6 h-6 md:w-8 md:h-8 text-yellow-800" />
                            </div>
                            <p className="font-extrabold text-gray-900 text-center truncate w-full text-sm md:text-base">{users[0].firstName}</p>
                            <p className="text-sm font-bold text-purple-700 mb-2">{users[0].interactions || 0} <span className="font-normal text-[10px] text-gray-400">int.</span></p>
                            <div className="w-full h-24 md:h-28 bg-gradient-to-t from-yellow-400 to-yellow-200 rounded-t-lg shadow-inner border-t border-yellow-500"></div>
                        </div>

                        {/* 3rd Place */}
                        <div className="flex flex-col items-center w-24 md:w-28">
                            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-orange-100 to-orange-300 border-4 border-white flex justify-center items-center mb-2 shadow-lg z-10 relative">
                                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">3</span>
                                <User className="w-5 h-5 md:w-6 md:h-6 text-orange-700" />
                            </div>
                            <p className="font-bold text-gray-800 text-center truncate w-full text-xs md:text-sm">{users[2].firstName}</p>
                            <p className="text-xs font-semibold text-purple-600 mb-2">{users[2].interactions || 0} <span className="font-normal text-[10px] text-gray-400">int.</span></p>
                            <div className="w-full h-12 md:h-16 bg-gradient-to-t from-orange-300 to-orange-200 rounded-t-lg shadow-inner border-t border-orange-400"></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Table Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Buscar por nombre o número Essen..."
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-600 font-medium text-sm uppercase tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Rango</th>
                                <th className="px-6 py-4">Vendedor</th>
                                <th className="px-6 py-4">Nro Essen</th>
                                <th className="px-6 py-4 text-right">Interacciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredUsers.map((user, index) => (
                                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${index === 0 ? 'bg-yellow-100 text-yellow-700' :
                                                index === 1 ? 'bg-gray-100 text-gray-700' :
                                                    index === 2 ? 'bg-orange-100 text-orange-700' :
                                                        'text-gray-500'
                                            }`}>
                                            {index + 1}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                                                <User className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
                                                <p className="text-xs text-gray-500">{user.license}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-gray-600 font-mono">{user.essenNumber}</td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex flex-col items-end">
                                            <span className="text-xl font-bold text-purple-600">{user.interactions || 0}</span>
                                            <div className="w-24 h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
                                                <div
                                                    className="h-full bg-purple-500 rounded-full"
                                                    style={{
                                                        width: `${totalInteractions > 0 ? ((user.interactions || 0) / (users[0].interactions || 1) * 100) : 0}%`
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {filteredUsers.length === 0 && (
                    <div className="p-12 text-center text-gray-500">
                        <p className="text-lg">No se encontraron resultados para "{searchTerm}"</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminInteractions;
